<template>
  <div ref="EinapageUsage" class="transparent">
    <h2>{{ $t("portfolio.einaPage.usage.registration.title") }}</h2>
    <hr class="secondary" />

    <h3>{{ $t("portfolio.einaPage.usage.registration.global.title") }}</h3>
    <p
      v-html="
        $t('portfolio.einaPage.usage.registration.global.paragraphA', {
          purple: 'text-white-purple'
        })
      "
    />
    <pre v-highlight class="highlight">
      <code class="javascript">
...
  import Vue from 'vue';
  import Einapage from 'einapage-vue';

  Vue.component('einapage', Einapage);

  new Vue({
...
      </code>
    </pre>
    <p
      v-html="
        $t('portfolio.einaPage.usage.registration.global.paragraphB', {
          purple: 'text-white-purple'
        })
      "
    />

    <h4>{{ $t("portfolio.einaPage.usage.registration.global.rootStyle.title") }}</h4>
    <p>{{ $t("portfolio.einaPage.usage.registration.global.rootStyle.paragraph") }}</p>
    <pre v-highlight class="highlight">
      <code class="javascript">
...
  import Vue from 'vue';
  import Einapage from 'einapage-vue';
  import 'einapage-vue/app/css/einapage.css';

  Vue.component('einapage', Einapage);

  new Vue({
...
      </code>
    </pre>

    <h4>{{ $t("portfolio.einaPage.usage.registration.global.componentStyle.title") }}</h4>
    <p
      v-html="
        $t('portfolio.einaPage.usage.registration.global.componentStyle.paragraph', {
          purple: 'text-white-purple'
        })
      "
    />
    <pre v-highlight class="highlight">
      <code class="xml">
...
    }
  }
  &#60;&#47;script&#62;

  &#60;style src="einapage-vue/app/css/einapage.css" /&#62;
...
      </code>
    </pre>
    <p>{{ $t("portfolio.einaPage.usage.registration.paragraphPreprocessor") }}</p>
    <pre v-highlight class="highlight">
      <code class="xml javascript">
...
    }
  }
  &#60;&#47;script&#62;

  &#60;style lang="scss"&#62;
    @import "~einapage-vue/app/css/einapage.css";
...
      </code>
    </pre>

    <h3>{{ $t("portfolio.einaPage.usage.registration.local.title") }}</h3>
    <p
      v-html="
        $t('portfolio.einaPage.usage.registration.local.paragraphA', {
          purple: 'text-white-purple'
        })
      "
    />
    <pre v-highlight class="highlight">
      <code class="xml javascript">
...
  &#60;script&#62;
    import einapage from "einapage-vue";

    export default {
      name: "MyFancyVueComponent",
      components: {
        einapage
      }
    }
  &#60;&#47;script&#62;
...
      </code>
    </pre>
    <p
      v-html="
        $t('portfolio.einaPage.usage.registration.local.paragraphB', {
          purple: 'text-white-purple'
        })
      "
    />

    <h4
      v-html="
        $t('portfolio.einaPage.usage.registration.local.scriptStyle.title', {
          purple: 'text-white-purple'
        })
      "
    />
    <p>{{ $t("portfolio.einaPage.usage.registration.local.scriptStyle.paragraph") }}</p>
    <pre v-highlight class="highlight">
      <code class="xml javascript">
...
  &#60;script&#62;
    import einapage from "einapage-vue";
    import 'einapage-vue/app/css/einapage.css';

    export default {
      name: "MyFancyVueComponent",
      components: {
        einapage
      }
    }
  &#60;&#47;script&#62;
...
      </code>
    </pre>

    <h4
      v-html="
        $t('portfolio.einaPage.usage.registration.local.styleStyle.title', {
          purple: 'text-white-purple'
        })
      "
    />
    <p
      v-html="
        $t('portfolio.einaPage.usage.registration.local.styleStyle.paragraph', {
          purple: 'text-white-purple'
        })
      "
    />
    <pre v-highlight class="highlight">
      <code class="xml javascript">
...
  &#60;script&#62;
    import einapage from "einapage-vue";

    export default {
      name: "MyFancyVueComponent",
      components: {
        einapage
      }
    }
  &#60;&#47;script&#62;

  &#60;style src="einapage-vue/app/css/einapage.css" /&#62;
...
      </code>
    </pre>
    <p>{{ $t("portfolio.einaPage.usage.registration.paragraphPreprocessor") }}</p>
    <pre v-highlight class="highlight">
      <code class="xml javascript">
...
  &#60;script&#62;
    import einapage from "einapage-vue";

    export default {
      name: "MyFancyVueComponent",
      components: {
        einapage
      }
    }
  &#60;&#47;script&#62;

  &#60;style lang="scss"&#62;
    @import "~einapage-vue/app/css/einapage.css";
...
      </code>
    </pre>

    <h2 v-html="$t('portfolio.einaPage.usage.props.title', { purple: 'text-white-purple' })" />
    <hr class="secondary" />
    <datatable :title="propsTitle" :data="propsData"></datatable>
    <p v-html="$t('portfolio.einaPage.usage.props.paragraph', { purple: 'text-white-purple' })" />
    <pre v-highlight class="highlight">
      <code class="xml javascript">
&#60;template&#62;
  &#60;einapage
  :currentrows="17"
  :currentpage="4"
  :rowcount="25100"
  :pagecount="1255"
  :perpage="[5, 10, 15, 20, 25]"
  perpageclass="custom-perpage"
  selectclass="custom-select"
  overlayclass="custom-overlay"
  detailsclass="custom-details"
  itemclass="custom-item"
  dataclass="custom-data"&#62;
  &#60;&#47;einapage&#62;
&#60;&#47;template&#62;

&#60;script&#62;
  import einapage from "einapage-vue";

  export default {
    name: "MyFancyVueComponent",
    components: {
      einapage
    }
  }
&#60;&#47;script&#62;
      </code>
    </pre>

    <h3>Prop: currentrows</h3>
    <p
      v-html="
        $t('portfolio.einaPage.usage.props.simplePropDescription', {
          purple: 'text-white-purple',
          prop: 'currentrows',
          value: 17
        })
      "
    />
    <einapage-instance :autoUpdate="false">
      <template v-slot:details="data">
        {{ $t("portfolio.einaPage.instance.showing") }}
        <span class="einapage-instance-container-highlighted">{{ data.currentrows }}</span>
        {{ $t("portfolio.einaPage.instance.of") }}
        {{ data.rowcount }}
        {{ $t("portfolio.einaPage.instance.rows") }}
        {{ data.currentpage }}
        {{ $t("portfolio.einaPage.instance.of") }}
        {{ data.pagecount }}.
      </template>
    </einapage-instance>

    <h3>Prop: currentpage</h3>
    <p
      v-html="
        $t('portfolio.einaPage.usage.props.simplePropDescription', {
          purple: 'text-white-purple',
          prop: 'currentpage',
          value: 4
        })
      "
    />
    <einapage-instance :autoUpdate="false">
      <template v-slot:details="data">
        {{ $t("portfolio.einaPage.instance.showing") }}
        {{ data.currentrows }}
        {{ $t("portfolio.einaPage.instance.of") }}
        {{ data.rowcount }}
        {{ $t("portfolio.einaPage.instance.rows") }}
        <span class="einapage-instance-container-highlighted">{{ data.currentpage }}</span>
        {{ $t("portfolio.einaPage.instance.of") }}
        {{ data.pagecount }}.
      </template>
    </einapage-instance>

    <h3>Prop: rowcount</h3>
    <p
      v-html="
        $t('portfolio.einaPage.usage.props.simplePropDescription', {
          purple: 'text-white-purple',
          prop: 'rowcount',
          value: 25100
        })
      "
    />
    <einapage-instance :autoUpdate="false">
      <template v-slot:details="data">
        {{ $t("portfolio.einaPage.instance.showing") }}
        {{ data.currentrows }}
        {{ $t("portfolio.einaPage.instance.of") }}
        <span class="einapage-instance-container-highlighted">{{ data.rowcount }}</span>
        {{ $t("portfolio.einaPage.instance.rows") }}
        {{ data.currentpage }}
        {{ $t("portfolio.einaPage.instance.of") }}
        {{ data.pagecount }}.
      </template>
    </einapage-instance>

    <h3>Prop: pagecount</h3>
    <p
      v-html="
        $t('portfolio.einaPage.usage.props.simplePropDescription', {
          purple: 'text-white-purple',
          prop: 'pagecount',
          value: 1255
        })
      "
    />
    <einapage-instance :autoUpdate="false">
      <template v-slot:details="data">
        {{ $t("portfolio.einaPage.instance.showing") }}
        {{ data.currentrows }}
        {{ $t("portfolio.einaPage.instance.of") }}
        {{ data.rowcount }}
        {{ $t("portfolio.einaPage.instance.rows") }}
        {{ data.currentpage }}
        {{ $t("portfolio.einaPage.instance.of") }}
        <span class="einapage-instance-container-highlighted">{{ data.pagecount }}</span
        >.
      </template>
    </einapage-instance>

    <h3>Prop: perpage</h3>
    <p v-html="$t('portfolio.einaPage.usage.props.perpage', { purple: 'text-white-purple' })" />
    <einapage-instance :autoUpdate="false" perpageclass="einapage-instance-container-highlighted" />

    <h3>Prop: perpageclass</h3>
    <p
      v-html="$t('portfolio.einaPage.usage.props.perpageclass', { purple: 'text-white-purple' })"
    />
    <pre v-highlight class="highlight">
      <code class="xml css javascript">
&#60;template&#62;
  &#60;einapage
  :currentrows="17"
  :currentpage="4"
  :rowcount="25100"
  :pagecount="1255"
  :perpage="[5, 10, 15, 20, 25]"
  perpageclass="custom-perpage"
  selectclass="custom-select"
  overlayclass="custom-overlay"
  detailsclass="custom-details"
  itemclass="custom-item"
  dataclass="custom-data"&#62;
  &#60;&#47;einapage&#62;
&#60;&#47;template&#62;

&#60;script&#62;
  import einapage from "einapage-vue";

  export default {
    name: "MyFancyVueComponent",
    components: {
      einapage
    }
  }
&#60;&#47;script&#62;

&#60;style&#62;
  .custom-perpage {
    border: 2px dashed #8e03c6;
    border-radius: 12px;
    padding: 0 8px;
  }
&#60;&#47;style&#62;
      </code>
    </pre>
    <einapage-instance :autoUpdate="false" perpageclass="custom-perpage" />

    <h3>Prop: selectclass</h3>
    <p v-html="$t('portfolio.einaPage.usage.props.selectclass', { purple: 'text-white-purple' })" />
    <pre v-highlight class="highlight">
      <code class="xml css javascript">
&#60;template&#62;
  &#60;einapage
  :currentrows="17"
  :currentpage="4"
  :rowcount="25100"
  :pagecount="1255"
  :perpage="[5, 10, 15, 20, 25]"
  perpageclass="custom-perpage"
  selectclass="custom-select"
  overlayclass="custom-overlay"
  detailsclass="custom-details"
  itemclass="custom-item"
  dataclass="custom-data"&#62;
  &#60;&#47;einapage&#62;
&#60;&#47;template&#62;

&#60;script&#62;
  import einapage from "einapage-vue";

  export default {
    name: "MyFancyVueComponent",
    components: {
      einapage
    }
  }
&#60;&#47;script&#62;

&#60;style&#62;
  .custom-select {
    background-color: #ade8f1;
  }
&#60;&#47;style&#62;
      </code>
    </pre>
    <einapage-instance :autoUpdate="false" selectclass="custom-select" />

    <h3>Prop: overlayclass</h3>
    <p
      v-html="$t('portfolio.einaPage.usage.props.overlayclass', { purple: 'text-white-purple' })"
    />
    <pre v-highlight class="highlight">
      <code class="xml css javascript">
&#60;template&#62;
  &#60;einapage
  :currentrows="17"
  :currentpage="4"
  :rowcount="25100"
  :pagecount="1255"
  :perpage="[5, 10, 15, 20, 25]"
  perpageclass="custom-perpage"
  selectclass="custom-select"
  overlayclass="custom-overlay"
  detailsclass="custom-details"
  itemclass="custom-item"
  dataclass="custom-data"&#62;
  &#60;&#47;einapage&#62;
&#60;&#47;template&#62;

&#60;script&#62;
  import einapage from "einapage-vue";

  export default {
    name: "MyFancyVueComponent",
    components: {
      einapage
    }
  }
&#60;&#47;script&#62;

&#60;style&#62;
  .custom-overlay {
    border: 2px dashed #8e03c6;
    border-radius: 12px;
    padding: 24px 16px;
  }
&#60;&#47;style&#62;
      </code>
    </pre>
    <einapage-instance :autoUpdate="false" overlayclass="custom-overlay" />

    <h3>Prop: detailsclass</h3>
    <p
      v-html="$t('portfolio.einaPage.usage.props.detailsclass', { purple: 'text-white-purple' })"
    />
    <pre v-highlight class="highlight">
      <code class="xml css javascript">
&#60;template&#62;
  &#60;einapage
  :currentrows="17"
  :currentpage="4"
  :rowcount="25100"
  :pagecount="1255"
  :perpage="[5, 10, 15, 20, 25]"
  perpageclass="custom-perpage"
  selectclass="custom-select"
  overlayclass="custom-overlay"
  detailsclass="custom-details"
  itemclass="custom-item"
  dataclass="custom-data"&#62;
  &#60;&#47;einapage&#62;
&#60;&#47;template&#62;

&#60;script&#62;
  import einapage from "einapage-vue";

  export default {
    name: "MyFancyVueComponent",
    components: {
      einapage
    }
  }
&#60;&#47;script&#62;

&#60;style&#62;
  .custom-details {
    border: 2px dashed #8e03c6;
    border-radius: 12px;
    padding: 0 8px;
  }
&#60;&#47;style&#62;
      </code>
    </pre>
    <einapage-instance :autoUpdate="false" detailsclass="custom-details" />

    <h3>Prop: itemclass</h3>
    <p v-html="$t('portfolio.einaPage.usage.props.itemclass', { purple: 'text-white-purple' })" />
    <pre v-highlight class="highlight">
      <code class="xml css javascript">
&#60;template&#62;
  &#60;einapage
  :currentrows="17"
  :currentpage="4"
  :rowcount="25100"
  :pagecount="1255"
  :perpage="[5, 10, 15, 20, 25]"
  perpageclass="custom-perpage"
  selectclass="custom-select"
  overlayclass="custom-overlay"
  detailsclass="custom-details"
  itemclass="custom-item"
  dataclass="custom-data"&#62;
  &#60;&#47;einapage&#62;
&#60;&#47;template&#62;

&#60;script&#62;
  import einapage from "einapage-vue";

  export default {
    name: "MyFancyVueComponent",
    components: {
      einapage
    }
  }
&#60;&#47;script&#62;

&#60;style&#62;
  .custom-item:not(.active) a {
    background-color: #ade8f1;
    border-color: #8e03c6;
    color: #8e03c6;
  }
  .custom-item.active a {
    background-color: #8e03c6;
    border-color: #ade8f1;
    color: #ade8f1;
  }
&#60;&#47;style&#62;
      </code>
    </pre>
    <einapage-instance :autoUpdate="false" itemclass="custom-item" />

    <h3>Prop: dataclass</h3>
    <p v-html="$t('portfolio.einaPage.usage.props.dataclass', { purple: 'text-white-purple' })" />
    <pre v-highlight class="highlight">
      <code class="xml css javascript">
&#60;template&#62;
  &#60;einapage
  :currentrows="17"
  :currentpage="4"
  :rowcount="25100"
  :pagecount="1255"
  :perpage="[5, 10, 15, 20, 25]"
  perpageclass="custom-perpage"
  selectclass="custom-select"
  overlayclass="custom-overlay"
  detailsclass="custom-details"
  itemclass="custom-item"
  dataclass="custom-data"&#62;
  &#60;&#47;einapage&#62;
&#60;&#47;template&#62;

&#60;script&#62;
  import einapage from "einapage-vue";

  export default {
    name: "MyFancyVueComponent",
    components: {
      einapage
    }
  }
&#60;&#47;script&#62;

&#60;style&#62;
  .custom-data {
    color: #8e03c6;
    font-style: italic;
  }
&#60;&#47;style&#62;
      </code>
    </pre>
    <einapage-instance :autoUpdate="false" dataclass="custom-data" />

    <h2 v-html="$t('portfolio.einaPage.usage.emitters.title', { purple: 'text-white-purple' })" />
    <hr class="secondary" />
    <p
      v-html="$t('portfolio.einaPage.usage.emitters.paragraphA', { purple: 'text-white-purple' })"
    />
    <pre v-highlight class="highlight">
      <code class="xml javascript">
&#60;template&#62;
  &#60;einapage
  v-on:setPage="setPage"
  :currentrows="17"
  :currentpage="currentpage"
  :rowcount="25100"
  :pagecount="1255"
  :perpage="[5, 10, 15, 20, 25]"&#62;
  &#60;&#47;einapage&#62;
&#60;&#47;template&#62;

&#60;script&#62;
  import einapage from "einapage-vue";

  export default {
    name: "MyFancyVueComponent",
    components: {
      einapage
    },
    data() {
      return {
        currentpage: 4,
      };
    },
    methods: {
      setPage(p) {
        this.currentpage = p;
        // {{ $t('portfolio.einaPage.usage.codeComment') }}
      }
    }
  }
&#60;&#47;script&#62;
      </code>
    </pre>
    <p
      v-html="$t('portfolio.einaPage.usage.emitters.paragraphB', { purple: 'text-white-purple' })"
    />
    <einapage-instance />

    <h2 v-html="$t('portfolio.einaPage.usage.vModel.title', { purple: 'text-white-purple' })" />
    <hr class="secondary" />
    <p v-html="$t('portfolio.einaPage.usage.vModel.paragraphA', { purple: 'text-white-purple' })" />
    <pre v-highlight class="highlight">
      <code class="xml javascript">
&#60;template&#62;
  &#60;einapage
  v-on:setPage="setPage"
  v-model="perpage.value"
  :currentrows="17"
  :currentpage="currentpage"
  :rowcount="25100"
  :pagecount="1255"
  :perpage="perpage.options"&#62;
  &#60;&#47;einapage&#62;
&#60;&#47;template&#62;

&#60;script&#62;
  import einapage from "einapage-vue";

  export default {
    name: "MyFancyVueComponent",
    components: {
      einapage
    },
    data() {
      return {
        currentpage: 4,
        perpage: {
          value: 10,
          options: [5, 10, 15, 20, 25]
        }
      };
    },
    watch: {
      ["perpage.value"](value) {
        this.setPerPageValue(value);
      }
    },
    methods: {
      setPage(p) {
        this.currentpage = p;
        // {{ $t('portfolio.einaPage.usage.codeComment') }}
      },
      setPerPageValue(value) {
        // {{ $t('portfolio.einaPage.usage.codeComment') }}
      }
    }
  }
&#60;&#47;script&#62;
      </code>
    </pre>
    <p v-html="$t('portfolio.einaPage.usage.vModel.paragraphB', { purple: 'text-white-purple' })" />
    <einapage-instance />

    <h2 v-html="$t('portfolio.einaPage.usage.slots.title', { purple: 'text-white-purple' })" />
    <hr class="secondary" />
    <datatable :title="slotsTitle" :data="slotsData"></datatable>
    <p v-html="$t('portfolio.einaPage.usage.slots.paragraphA', { purple: 'text-white-purple' })" />
    <pre v-highlight class="highlight">
      <code class="xml javascript">
&#60;template&#62;
  &#60;einapage
  v-on:setPage="setPage"
  v-model="perpage.value"
  :currentrows="17"
  :currentpage="currentpage"
  :rowcount="25100"
  :pagecount="1255"
  :perpage="perpage.options"&#62;
    &#60;template v-slot:details="data"&#62;
      {{ $t('portfolio.einaPage.usage.slots.instance.currentrows.description') }} {{ $t('portfolio.einaPage.usage.slots.instance.currentrows.value') }}
      {{ $t('portfolio.einaPage.usage.slots.instance.currentpage.description') }} {{ $t('portfolio.einaPage.usage.slots.instance.currentpage.value') }}
      {{ $t('portfolio.einaPage.usage.slots.instance.rowcount.description') }} {{ $t('portfolio.einaPage.usage.slots.instance.rowcount.value') }}
      {{ $t('portfolio.einaPage.usage.slots.instance.pagecount.description') }} {{ $t('portfolio.einaPage.usage.slots.instance.pagecount.value') }}
    &#60;&#47;template&#62;
    &#60;template v-slot:perPage="data"&#62;
      {{ $t('portfolio.einaPage.usage.slots.instance.perpage.description') }} {{ $t('portfolio.einaPage.usage.slots.instance.perpage.value') }}
    &#60;&#47;template&#62;
    &#60;template v-slot:firstArrow&#62;
      {{ $t('portfolio.einaPage.usage.slots.instance.first') }}
    &#60;&#47;template&#62;
    &#60;template v-slot:lastArrow&#62;
      {{ $t('portfolio.einaPage.usage.slots.instance.last') }}
    &#60;&#47;template&#62;
  &#60;&#47;einapage&#62;
&#60;&#47;template&#62;

&#60;script&#62;
  import einapage from "einapage-vue";

  export default {
    name: "MyFancyVueComponent",
    components: {
      einapage
    },
    data() {
      return {
        currentpage: 4,
        perpage: {
          value: 10,
          options: [5, 10, 15, 20, 25]
        }
      };
    },
    watch: {
      ["perpage.value"](value) {
        this.setPerPageValue(value);
      }
    },
    methods: {
      setPage(p) {
        this.currentpage = p;
        // {{ $t('portfolio.einaPage.usage.codeComment') }}
      },
      setPerPageValue(value) {
        // {{ $t('portfolio.einaPage.usage.codeComment') }}
      }
    }
  }
&#60;&#47;script&#62;
      </code>
    </pre>
    <p>{{ $t("portfolio.einaPage.usage.slots.paragraphB") }}</p>
    <einapage-instance>
      <template v-slot:details="data">
        {{ $t("portfolio.einaPage.usage.slots.instance.currentrows.description") }}
        {{ data.currentrows }},
        {{ $t("portfolio.einaPage.usage.slots.instance.currentpage.description") }}
        {{ data.currentpage }},
        {{ $t("portfolio.einaPage.usage.slots.instance.rowcount.description") }}
        {{ data.rowcount }},
        {{ $t("portfolio.einaPage.usage.slots.instance.pagecount.description") }}
        {{ data.pagecount }}
      </template>
      <template v-slot:perPage="data">
        {{ $t("portfolio.einaPage.usage.slots.instance.perpage.description") }} {{ data.perpage }}
      </template>
      <template v-slot:firstArrow>
        {{ $t("portfolio.einaPage.usage.slots.instance.first") }}
      </template>
      <template v-slot:lastArrow>
        {{ $t("portfolio.einaPage.usage.slots.instance.last") }}
      </template>
    </einapage-instance>
  </div>
</template>

<script>
const Datatable = () =>
  import(/* webpackChunkName: "Datatable" */ "@/components/Global/Datatable.vue");
const EinapageInstance = () =>
  import(/* webpackChunkName: "EinapageInstance" */ "@/components/Portfolio/EinapageInstance");

export default {
  name: "EinapageUsage",
  components: {
    Datatable,
    EinapageInstance
  },
  computed: {
    /*
     * @computed - propsTitle
     * Adds table header values to props data table
     */
    propsTitle() {
      return [
        {
          name: "name",
          displayName: this.$t("portfolio.einaPage.usage.propsTable.title.columnName")
        },
        {
          name: "description",
          displayName: this.$t("portfolio.einaPage.usage.propsTable.title.columnDescription")
        },
        {
          name: "datatype",
          displayName: this.$t("portfolio.einaPage.usage.propsTable.title.columnDatatype")
        },
        {
          name: "default",
          displayName: this.$t("portfolio.einaPage.usage.propsTable.title.columnDefault")
        }
      ];
    },
    /*
     * @computed - propsData
     * Adds table data values to props data table
     */
    propsData() {
      return [
        {
          name: "currentrows",
          description: this.$t("portfolio.einaPage.usage.propsTable.data.rowCurrentrows"),
          datatype: "number",
          default: "0"
        },
        {
          name: "rowcount",
          description: this.$t("portfolio.einaPage.usage.propsTable.data.rowRowcount"),
          datatype: "number",
          default: "1"
        },
        {
          name: "currentpage",
          description: "Pagina actual.",
          datatype: "number",
          default: "1"
        },
        {
          name: "pagecount",
          description: this.$t("portfolio.einaPage.usage.propsTable.data.rowPagecount"),
          datatype: "number",
          default: "0"
        },
        {
          name: "perpage",
          description: this.$t("portfolio.einaPage.usage.propsTable.data.rowPerpage"),
          datatype: "array",
          default: "undefined"
        },
        {
          name: "perpageclass",
          description: this.$t("portfolio.einaPage.usage.propsTable.data.rowPerpageclass"),
          datatype: "string",
          default: "undefined"
        },
        {
          name: "selectclass",
          description: this.$t("portfolio.einaPage.usage.propsTable.data.rowSelectclass"),
          datatype: "string",
          default: "undefined"
        },
        {
          name: "overlayclass",
          description: this.$t("portfolio.einaPage.usage.propsTable.data.rowOverlayclass"),
          datatype: "string",
          default: "undefined"
        },
        {
          name: "dataclass",
          description: this.$t("portfolio.einaPage.usage.propsTable.data.rowDataclass"),
          datatype: "string",
          default: "undefined"
        },
        {
          name: "detailsclass",
          description: this.$t("portfolio.einaPage.usage.propsTable.data.rowDetailsclass"),
          datatype: "string",
          default: "undefined"
        },
        {
          name: "itemclass",
          description: this.$t("portfolio.einaPage.usage.propsTable.data.rowItemclass"),
          datatype: "string",
          default: "undefined"
        }
      ];
    },
    /*
     * @computed - slotsTitle
     * Adds table header values to slots data table
     */
    slotsTitle() {
      return [
        {
          name: "name",
          displayName: this.$t("portfolio.einaPage.usage.slotsTable.title.columnName")
        },
        {
          name: "description",
          displayName: this.$t("portfolio.einaPage.usage.slotsTable.title.columnDescription")
        },
        {
          name: "scope",
          displayName: this.$t("portfolio.einaPage.usage.slotsTable.title.columnScope")
        }
      ];
    },
    /*
     * @computed - slotsData
     * Adds table data values to slots data table
     */
    slotsData() {
      return [
        {
          name: "details",
          description: this.$t("portfolio.einaPage.usage.slotsTable.data.rowDetails"),
          scope: "currentrows, currentpage, rowcount, pagecount"
        },
        {
          name: "perPage",
          description: this.$t("portfolio.einaPage.usage.slotsTable.data.rowPerPage"),
          scope: "perpage"
        },
        {
          name: "firstArrow",
          description: this.$t("portfolio.einaPage.usage.slotsTable.data.rowFirstArrow"),
          scope: "n/a"
        },
        {
          name: "lastArrow",
          description: this.$t("portfolio.einaPage.usage.slotsTable.data.rowLastArrow"),
          scope: "n/a"
        }
      ];
    }
  }
};
</script>
